/**
 * map_raster.ts — the World Atlas tiled raster renderer (DOM side): a pool of
 * map_raster_worker threads, the ImageBitmap tile cache, the request queue,
 * and ONE draw call per visible tile. The module hands it a view and a layer
 * every frame; it draws what it has (exact tiles, else a cached ancestor
 * stretched — never a blank), asks the workers for what it lacks, coarse
 * level first, centre-out, and marks the map dirty as tiles land.
 *
 * LAWS
 * · The queue is REBUILT from the current view every draw: a tile that
 *   scrolled out of view before a worker took it is never computed. Only
 *   jobs already posted (≤ 4 per worker) run to completion.
 * · Tiles are keyed by (layer, level, tx, tz[, bucket]); the weather layers
 *   carry a 60 s bucket, the conflict layers a snapshot id in the layer name,
 *   so a stale layer is simply never looked up — and its bitmaps age out of
 *   the LRU on their own.
 * · A previous-bucket weather tile is a legal fallback: the fronts move
 *   ~11 m/s, and a 60 s-old sky beats a hole in the map. Its EXACT tile
 *   outranks the new bucket's coarse ancestor (sharp before blurred), and
 *   the conflict layers keep their previous generation the same way.
 * · One want list PER LAYER; the terrain underlay is pumped first.
 * · A worker that errors or a tile that throws comes back as a failure:
 *   the slot frees, the tile is asked again at most twice.
 */
import { TileCache, tileKey, tileRect, tilesInView, levelFor, planRequests, TILE_PX, type TileId } from './map_tiles.js';
import { SAMPLE_PX, SNAPSHOT_LAYERS, type RasterLayer, type TileJob, type ConflictSnapshot } from './map_layers.js';

export interface RasterView { cx: number; cz: number; W: number; H: number; scale: number }
export interface RasterStats {
  workers: number; cached: number; queued: number; inFlight: number;
  jobsDone: number; msWorker: number; evicted: number;
  /** The last draw: tiles drawn exactly / from an ancestor / nothing at all, and the level. */
  exact: number; fallback: number; missing: number; level: number;
  /** ms from the first request of a (layer, view) until every visible tile was exact (the last completed wait). */
  lastFillMs: number;
  /** performance.now() of the first job posted / the first tile landed (a probe separates worker latency from a starved main thread). */
  tFirstJob: number; tFirstTile: number;
  /** The layer the exact / fallback / missing counts describe (the last draw call of the frame). */
  layer: string;
}

interface Req { key: string; tile: TileId; layer: RasterLayer; bucket: number; job: TileJob; wi?: number }
type Bitmap = ImageBitmap | HTMLCanvasElement;
type SnapLayer = 'civ' | 'war';

export class MapRaster {
  private readonly workers: Worker[] = [];
  private readonly busy: number[] = [];
  private readonly cache = new TileCache<Bitmap>(640);
  private queue: Req[] = [];
  private readonly inflight = new Map<number, Req>();
  private readonly requested = new Set<string>();
  /** Keys whose job failed twice (a sampler throw, a dead worker): never asked for again this world. */
  private readonly failed = new Map<string, number>();
  /** When each layer last planned — a layer nobody drew for a moment has its queued wants dropped. */
  private readonly lastPlanAt = new Map<RasterLayer, number>();
  private nextId = 1;
  private seed = '';
  private sea = 0;
  /** THE CONFLICT GENERATIONS, one per layer (bug sweep 2026-09-23): the territory layer reads only the
   *  settlements and the war layer only the wars and clashes, but ONE digest bumped both — any clash
   *  ending, any battle engaging, wiped the territory layer and re-rendered the whole view. The previous
   *  generation stays drawable (the fallback) until the new one covers the view. */
  private readonly snapIds = new Map<SnapLayer, number>();
  private readonly prevSnapIds = new Map<SnapLayer, number>();
  private readonly snapDigests = new Map<SnapLayer, string>();
  private snapCounter = 0;
  private fillStart = 0;
  readonly stats: RasterStats = { workers: 0, cached: 0, queued: 0, inFlight: 0, jobsDone: 0, msWorker: 0, evicted: 0, exact: 0, fallback: 0, missing: 0, level: 0, lastFillMs: 0, tFirstJob: 0, tFirstTile: 0, layer: '' };
  /** Called when a tile lands (the owner marks its canvas dirty). */
  onTile: (() => void) | null = null;
  /** Jobs committed per worker. Small, so a tile that scrolls out of view is rarely computed; not 1–2, because the
   *  next job is posted from the main thread's message handler — on a slow frame cadence a shallow queue idles the pool. */
  private static readonly PER_WORKER = 4;
  /** A layer that has not planned for this long is no longer on screen: its queued (unposted) wants go. */
  private static readonly WANT_TTL_MS = 250;

  constructor(makeWorker: () => Worker, poolSize: number) {
    for (let i = 0; i < Math.max(1, poolSize); i++) {
      const w = makeWorker();
      w.onmessage = (e: MessageEvent<{ id: number; n: number; px?: Uint8ClampedArray; ms: number; error?: string }>) => this.onResult(i, e.data);
      // A dead worker never answers: without this its jobs held `busy`, `inflight` and `requested` for good —
      // those tiles were never asked for again, and four losses retired the worker (bug sweep 2026-09-23).
      w.onerror = (e) => { console.warn('[MapRaster] worker error', e.message); this.releaseWorker(i); };
      w.onmessageerror = () => this.releaseWorker(i);
      this.workers.push(w);
      this.busy.push(0);
    }
    this.stats.workers = this.workers.length;
  }

  /** The world changed: nothing cached applies. */
  setWorld(seed: string, sea: number): void {
    if (seed === this.seed && sea === this.sea) return;
    this.seed = seed; this.sea = sea;
    this.cache.clear((b) => this.free(b));
    this.queue = [];
    this.requested.clear();
    this.failed.clear();
  }

  /** The conflict layers' rows changed: a new generation for each layer whose OWN digest moved. */
  setSnapshot(snapshot: ConflictSnapshot, digests: Record<SnapLayer, string>): void {
    const bumped: SnapLayer[] = [];
    for (const layer of ['civ', 'war'] as const) {
      if (digests[layer] === this.snapDigests.get(layer)) continue;
      this.snapDigests.set(layer, digests[layer]);
      bumped.push(layer);
    }
    if (bumped.length === 0) return;
    const id = ++this.snapCounter;
    for (const layer of bumped) {
      const cur = this.snapIds.get(layer);
      const older = this.prevSnapIds.get(layer);
      // Two generations back is never drawn again: free it (the `:` keeps `civ@5` from matching `civ@50`).
      if (older !== undefined) this.stats.evicted += this.cache.dropPrefix(`${layer}@${older}:`, (b) => this.free(b));
      if (cur !== undefined) this.prevSnapIds.set(layer, cur);
      this.snapIds.set(layer, id);
      this.queue = this.queue.filter((r) => r.layer !== layer);
      for (const k of [...this.requested]) if (k.startsWith(`${layer}@`)) this.requested.delete(k);
    }
    // The workers keep exactly the generations a job can still name.
    const keep = [...new Set(['civ', 'war'].map((l) => this.snapIds.get(l as SnapLayer)).filter((v): v is number => v !== undefined))];
    for (const w of this.workers) w.postMessage({ kind: 'snapshot', snapId: id, snapshot, keep });
  }

  private layerKey(layer: RasterLayer): string {
    return SNAPSHOT_LAYERS.has(layer) ? `${layer}@${this.snapIds.get(layer as SnapLayer) ?? 0}` : layer;
  }
  /** The previous refresh a hole may borrow: last minute's sky, the last conflict generation. */
  private fallbackOf(layer: RasterLayer, bucket: number): { layer: string; bucket: number } | undefined {
    if (SNAPSHOT_LAYERS.has(layer)) {
      const prev = this.prevSnapIds.get(layer as SnapLayer);
      return prev !== undefined ? { layer: `${layer}@${prev}`, bucket: 0 } : undefined;
    }
    return bucket ? { layer, bucket: bucket - 1 } : undefined;
  }

  /**
   * Draw the layer over the view. `bucket` keys the weather layers' 60 s
   * refresh (0 for timeless layers); `tSec`/`tod` are the weather clock.
   */
  draw(g: CanvasRenderingContext2D, layer: RasterLayer, view: RasterView, alpha: number, opts: { bucket?: number; tSec?: number; tod?: number } = {}): void {
    if (!this.seed) return;
    const bucket = opts.bucket ?? 0;
    const screenMpp = 1 / view.scale;
    const L = levelFor(screenMpp, SAMPLE_PX[layer]);
    const lk = this.layerKey(layer);
    const prev = this.fallbackOf(layer, bucket);
    const tiles = tilesInView(L, view.cx, view.cz, view.W, view.H, screenMpp, 0);
    const sx = (wx: number): number => (wx - view.cx) * view.scale + view.W / 2;
    const sz = (wz: number): number => (wz - view.cz) * view.scale + view.H / 2;
    let exact = 0, fallback = 0, missing = 0;
    g.imageSmoothingEnabled = true;
    g.globalAlpha = alpha;
    for (const t of tiles) {
      const r = tileRect(t);
      // Seamless edges: round both edges, never (round(x), round(w)).
      const dx0 = Math.round(sx(r.x0)), dx1 = Math.round(sx(r.x0 + r.span));
      const dy0 = Math.round(sz(r.z0)), dy1 = Math.round(sz(r.z0 + r.span));
      if (dx1 <= 0 || dy1 <= 0 || dx0 >= view.W || dy0 >= view.H) continue;
      // Best first: this refresh's exact tile, the previous refresh's EXACT tile (a sharp minute-old sky beats
      // a blurred current one — the coarse ancestor landed first and blurred the layer for 1–3 s every minute),
      // then this refresh's ancestor, then the previous refresh's.
      const hit = this.cache.lookup(lk, t, bucket, 0)
        ?? (prev ? this.cache.lookup(prev.layer, t, prev.bucket, 0) : null)
        ?? this.cache.lookup(lk, t, bucket)
        ?? (prev ? this.cache.lookup(prev.layer, t, prev.bucket) : null);
      if (!hit) { missing++; continue; }
      const n = TILE_PX;
      g.drawImage(hit.value, hit.u0 * n, hit.v0 * n, hit.frac * n, hit.frac * n, dx0, dy0, dx1 - dx0, dy1 - dy0);
      if (hit.exact) exact++; else fallback++;
    }
    g.globalAlpha = 1;
    this.stats.exact = exact; this.stats.fallback = fallback; this.stats.missing = missing; this.stats.level = L; this.stats.layer = layer;
    this.plan(layer, lk, bucket, L, view, screenMpp, opts, prev);
  }

  /** Rebuild THIS layer's wants for the current view (coarse level first, centre-out) and feed the workers. */
  private plan(layer: RasterLayer, lk: string, bucket: number, L: number, view: RasterView, screenMpp: number, opts: { tSec?: number; tod?: number }, prev?: { layer: string; bucket: number }): void {
    const p = planRequests(this.cache, lk, bucket, L, view.cx, view.cz, view.W, view.H, screenMpp, 2, 0.5, prev);
    const now = performance.now();
    this.lastPlanAt.set(layer, now);
    const keep = new Set<string>();
    const reqs: Req[] = [];
    const push = (t: TileId): void => {
      const key = tileKey(lk, t, bucket);
      keep.add(key);
      if (this.requested.has(key)) return; // queued already or in flight
      if ((this.failed.get(key) ?? 0) >= 2) return; // failed twice — never a request loop
      const r = tileRect(t);
      const job: TileJob = { id: 0, layer, seed: this.seed, sea: this.sea, x0: r.x0, z0: r.z0, span: r.span, n: TILE_PX, tSec: opts.tSec, tod: opts.tod };
      if (SNAPSHOT_LAYERS.has(layer)) job.snapId = this.snapIds.get(layer as SnapLayer);
      reqs.push({ key, tile: t, layer, bucket, job });
    };
    for (const t of p.coarse) push(t);
    for (const t of p.fine) push(t);
    // ONE WANT LIST PER LAYER (bug sweep 2026-09-23): the terrain and the overlay are planned in the same frame,
    // and each plan used to drop the OTHER's queued tiles — a pan's terrain waited behind weather tiles that cost
    // 3–6× more per sample. A plan now replaces only its own layer's wants; a layer not drawn for a moment
    // (switched off) loses its queued wants. In-flight jobs finish on their own.
    const stale = this.queue.filter((r) => (r.layer === layer ? !keep.has(r.key) : now - (this.lastPlanAt.get(r.layer) ?? 0) > MapRaster.WANT_TTL_MS));
    for (const r of stale) this.requested.delete(r.key);
    const staleSet = new Set(stale);
    this.queue = [...this.queue.filter((r) => !staleSet.has(r)), ...reqs];
    for (const r of reqs) this.requested.add(r.key);
    // The terrain underlay first: it is the map, and it is the cheap one.
    if (reqs.length > 0) this.queue.sort((a, b) => (a.layer === 'terrain' ? 0 : 1) - (b.layer === 'terrain' ? 0 : 1));
    if (p.fine.length > 0 && this.fillStart === 0) this.fillStart = now;
    if (p.fine.length === 0 && p.coarse.length === 0 && this.fillStart !== 0) { this.stats.lastFillMs = now - this.fillStart; this.fillStart = 0; }
    this.pump();
    this.stats.queued = this.queue.length;
    this.stats.inFlight = this.inflight.size;
    this.stats.cached = this.cache.size;
  }

  private pump(): void {
    while (this.queue.length > 0) {
      let wi = -1;
      for (let i = 0; i < this.workers.length; i++) if (this.busy[i] < MapRaster.PER_WORKER && (wi < 0 || this.busy[i] < this.busy[wi])) wi = i;
      if (wi < 0) return;
      const r = this.queue.shift()!;
      r.job.id = this.nextId++;
      r.wi = wi;
      this.inflight.set(r.job.id, r);
      this.busy[wi]++;
      if (this.stats.tFirstJob === 0) this.stats.tFirstJob = performance.now();
      this.workers[wi].postMessage(r.job);
    }
  }

  /** A job that came back without a tile: free its slot, count the failure, let the next plan ask again (twice at most). */
  private jobFailed(r: Req): void {
    this.requested.delete(r.key);
    this.failed.set(r.key, (this.failed.get(r.key) ?? 0) + 1);
  }

  /** A worker errored: every job it held comes back as a failure (its slots free for the rest of the pool). */
  private releaseWorker(wi: number): void {
    for (const [id, r] of [...this.inflight]) {
      if (r.wi !== wi) continue;
      this.inflight.delete(id);
      this.jobFailed(r);
    }
    this.busy[wi] = 0;
    this.stats.inFlight = this.inflight.size;
    this.onTile?.(); // the owner redraws, which re-plans
  }

  private onResult(wi: number, res: { id: number; n: number; px?: Uint8ClampedArray; ms: number; error?: string }): void {
    this.busy[wi] = Math.max(0, this.busy[wi] - 1);
    const r = this.inflight.get(res.id);
    this.inflight.delete(res.id);
    this.stats.jobsDone++;
    this.stats.msWorker += res.ms;
    if (this.stats.tFirstTile === 0) this.stats.tFirstTile = performance.now();
    if (r && (res.error || !res.px)) { console.warn('[MapRaster] tile failed', r.key, res.error); this.jobFailed(r); }
    else if (r && res.px) {
      this.requested.delete(r.key);
      const img = new ImageData(res.px, res.n, res.n);
      const done = (b: Bitmap): void => { this.cache.set(r.key, b, (ev) => { this.free(ev); this.stats.evicted++; }); this.stats.cached = this.cache.size; this.onTile?.(); };
      if (typeof createImageBitmap === 'function') {
        createImageBitmap(img).then(done, () => done(MapRaster.toCanvas(img)));
      } else done(MapRaster.toCanvas(img));
    }
    this.pump();
    this.stats.queued = this.queue.length;
    this.stats.inFlight = this.inflight.size;
  }

  private static toCanvas(img: ImageData): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    c.getContext('2d')!.putImageData(img, 0, 0);
    return c;
  }

  private free(b: Bitmap): void { if ('close' in b && typeof (b as ImageBitmap).close === 'function') (b as ImageBitmap).close(); }

  dispose(): void {
    for (const w of this.workers) w.terminate();
    this.workers.length = 0;
    this.cache.clear((b) => this.free(b));
    this.queue = [];
    this.inflight.clear();
    this.requested.clear();
    this.failed.clear();
  }
}

/** The pool size for this machine: leave the game its cores. */
export function rasterPoolSize(): number {
  const hc = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4;
  return Math.max(1, Math.min(3, hc - 2));
}
